import { useEffect, useRef, useState } from 'react'
import { Plus, X } from 'lucide-react'
import type { AccountSummary } from '@/api/accounts'
import { AmountCalculatorInput } from '@/components/AmountCalculatorInput'
import { Button } from '@/components/ui/button'
import { Select } from '@/components/ui/select'
import { useFormatCurrency } from '@/hooks/useFormatCurrency'
import { fillRemainder, type PartRow } from '@/lib/paymentParts'
import { fromPaise, toPaise } from '@/lib/splitMath'
import { transactableAccounts } from './formLogic'

export function PaymentSourcesSection({
  totalRupees,
  accounts,
  value,
  onChange,
}: {
  totalRupees: number
  accounts: AccountSummary[]
  value: PartRow[]
  onChange: (rows: PartRow[]) => void
}) {
  const { format } = useFormatCurrency()
  const totalPaise = toPaise(totalRupees || 0)
  const options = transactableAccounts(accounts)

  // Read through refs so only a change in the total re-fills; a new onChange identity must not.
  const latest = useRef({ value, onChange })
  useEffect(() => {
    latest.current = { value, onChange }
  })
  const lastTotal = useRef(totalPaise)
  useEffect(() => {
    if (lastTotal.current === totalPaise) return
    lastTotal.current = totalPaise
    const { value: rows, onChange: change } = latest.current
    // Until the user types a part, the first account simply carries the whole bill.
    const next = rows.some((r) => r.edited)
      ? fillRemainder(totalPaise, rows)
      : rows.map((r, i) => ({ ...r, amountPaise: i === 0 ? totalPaise : 0 }))
    if (next.some((r, i) => r.amountPaise !== rows[i].amountPaise)) change(next)
  }, [totalPaise])

  function setAccount(index: number, accountId: string) {
    onChange(value.map((r, i) => (i === index ? { ...r, accountId } : r)))
  }

  function setAmount(index: number, raw: string) {
    const rows = value.map((r, i) =>
      i === index ? { ...r, amountPaise: toPaise(Number(raw) || 0), edited: true } : r
    )
    onChange(fillRemainder(totalPaise, rows))
  }

  function remove(index: number) {
    onChange(
      fillRemainder(
        totalPaise,
        value.filter((_, i) => i !== index)
      )
    )
  }

  const leftPaise = totalPaise - value.reduce((s, r) => s + r.amountPaise, 0)

  return (
    <div className="space-y-3 rounded-lg border border-[var(--fl-border)] px-3 py-3">
      {value.map((row, i) => (
        <div key={i} className="flex items-center gap-2">
          <div className="min-w-0 flex-1">
            <Select
              aria-label={`Account for part ${i + 1}`}
              value={row.accountId}
              onChange={(v) => setAccount(i, v)}
            >
              <option value="">Select account</option>
              {options.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.name}
                </option>
              ))}
            </Select>
          </div>
          <div className="w-32 shrink-0">
            <PartAmount
              label={`Amount for part ${i + 1}`}
              amountPaise={row.amountPaise}
              onChange={(v) => setAmount(i, v)}
            />
          </div>
          {value.length > 2 && (
            <Button
              type="button"
              variant="ghost"
              aria-label={`Remove part ${i + 1}`}
              onClick={() => remove(i)}
              className="h-8 w-8 shrink-0 p-0 text-[var(--fl-text-2)]"
            >
              <X size={14} />
            </Button>
          )}
        </div>
      ))}
      <Button
        type="button"
        variant="ghost"
        onClick={() =>
          onChange(
            fillRemainder(totalPaise, [...value, { accountId: '', amountPaise: 0, edited: false }])
          )
        }
        className="h-auto gap-1 px-1 py-1 text-sm text-[var(--fl-primary-text)]"
      >
        <Plus size={14} /> Add another account
      </Button>
      {leftPaise !== 0 && (
        <p className="text-xs text-[var(--fl-text-2)]">
          {leftPaise > 0
            ? `${format(fromPaise(leftPaise))} left to assign`
            : `${format(fromPaise(-leftPaise))} too much`}
        </p>
      )}
    </div>
  )
}

/** Keeps what the user typed ("0." on the way to "0.5") while it still means the stored amount. */
function PartAmount({
  label,
  amountPaise,
  onChange,
}: {
  label: string
  amountPaise: number
  onChange: (raw: string) => void
}) {
  const [draft, setDraft] = useState<string | null>(null)
  const inSync = draft !== null && toPaise(Number(draft) || 0) === amountPaise
  const shown = inSync ? draft : amountPaise ? String(fromPaise(amountPaise)) : ''
  return (
    <AmountCalculatorInput
      aria-label={label}
      value={shown}
      onChange={(v) => {
        setDraft(v)
        onChange(v)
      }}
      placeholder="0.00"
    />
  )
}
